import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import LabelPrintModal from '../components/LabelPrintModal';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const parseCsv = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (!lines.length) return { rows: [], error: 'CSV is empty' };

  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const orderIdx = header.indexOf('order_number');
  const trackingIdx = header.indexOf('tracking_number');
  if (orderIdx === -1 || trackingIdx === -1) {
    return { rows: [], error: 'CSV must have headers: order_number, tracking_number' };
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = lines[i].split(',');
    const order_number = (parts[orderIdx] || '').trim();
    const tracking_number = (parts[trackingIdx] || '').trim();
    rows.push({ order_number, tracking_number, line: i + 1 });
  }
  return { rows, error: '' };
};

export default function AdminShipments() {
  const navigate = useNavigate();
  const jobStatusRef = useRef(null);
  const pickupStatusRef = useRef(null);
  const statusRef = useRef(null);
  const refreshedCandidatesForJobRef = useRef({});

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [fileName, setFileName] = useState('');
  const [rawRows, setRawRows] = useState([]);

  const [sendEmail, setSendEmail] = useState(true);
  const [resendIfAlreadyShipped, setResendIfAlreadyShipped] = useState(false);

  const [labelLookbackDays, setLabelLookbackDays] = useState('7');
  const [labelCandidates, setLabelCandidates] = useState([]);
  const [labelCursor, setLabelCursor] = useState('');
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState({});

  const [globalMode, setGlobalMode] = useState('preset');
  const [globalPreset, setGlobalPreset] = useState('SMALL');
  const [globalCarrier, setGlobalCarrier] = useState('ANY');
  const [globalSpeed, setGlobalSpeed] = useState('STANDARD');
  const [rowOverrides, setRowOverrides] = useState({});

  const [labelPickupEnabled, setLabelPickupEnabled] = useState(false);
  const [labelPickupCarrier, setLabelPickupCarrier] = useState('ANY');
  const [labelPickupMin, setLabelPickupMin] = useState('');
  const [labelPickupMax, setLabelPickupMax] = useState('');
  const [labelPickupInstructions, setLabelPickupInstructions] = useState('');
  const [labelPickupUseAddressOverride, setLabelPickupUseAddressOverride] = useState(false);
  const [labelPickupStreet1, setLabelPickupStreet1] = useState('');
  const [labelPickupStreet2, setLabelPickupStreet2] = useState('');
  const [labelPickupCity, setLabelPickupCity] = useState('');
  const [labelPickupState, setLabelPickupState] = useState('');
  const [labelPickupZip, setLabelPickupZip] = useState('');
  const [labelPickupCountry, setLabelPickupCountry] = useState('US');
  const [labelPickupPhone, setLabelPickupPhone] = useState('');
  const [labelPickupEmail, setLabelPickupEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const [pickupSubmitting, setPickupSubmitting] = useState(false);
  const [pickupOrderNumber, setPickupOrderNumber] = useState('');
  const [pickupCarrier, setPickupCarrier] = useState('ANY');
  const [pickupLockedCarrier, setPickupLockedCarrier] = useState('');
  const [pickupCarrierLoading, setPickupCarrierLoading] = useState(false);
  const [pickupMin, setPickupMin] = useState('');
  const [pickupMax, setPickupMax] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [pickupResult, setPickupResult] = useState(null);
  const [pickupError, setPickupError] = useState('');
  const [pickupStatusMessage, setPickupStatusMessage] = useState('');
  const [pickupFieldErrors, setPickupFieldErrors] = useState({});

  const [pickupUseAddressOverride, setPickupUseAddressOverride] = useState(false);
  const [pickupStreet1, setPickupStreet1] = useState('');
  const [pickupStreet2, setPickupStreet2] = useState('');
  const [pickupCity, setPickupCity] = useState('');
  const [pickupState, setPickupState] = useState('');
  const [pickupZip, setPickupZip] = useState('');
  const [pickupCountry, setPickupCountry] = useState('US');
  const [pickupPhone, setPickupPhone] = useState('');
  const [pickupEmail, setPickupEmail] = useState('');

  const [jobId, setJobId] = useState('');
  const [job, setJob] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportRows, setReportRows] = useState([]);

  const [printOpen, setPrintOpen] = useState(false);
  const [printLabelUrl, setPrintLabelUrl] = useState('');

  const scrollToJobStatus = () => {
    const el = jobStatusRef.current;
    if (!el) return;
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (typeof el.focus === 'function') el.focus({ preventScroll: true });
      } catch {
      }
    }, 50);
  };

  const scrollToPickupStatus = () => {
    const el = pickupStatusRef.current;
    if (!el) return;
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (typeof el.focus === 'function') {
          el.focus();
        }
      } catch {
        // ignore
      }
    }, 50);
  };

  useEffect(() => {
    if (!jobId) return;
    scrollToJobStatus();
  }, [jobId]);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const raw = String(pickupOrderNumber || '').trim();
    if (!raw) {
      setPickupLockedCarrier('');
      setPickupCarrier('ANY');
      setPickupCarrierLoading(false);
      return;
    }
    if (!API_BASE_URL) {
      setPickupLockedCarrier('');
      setPickupCarrier('ANY');
      setPickupCarrierLoading(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setPickupCarrierLoading(true);
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        const url = new URL(`${API_BASE_URL}/admin/shipments/pickups/label-carrier`);
        url.searchParams.set('order_number', raw);

        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok) {
          setPickupLockedCarrier('');
          return;
        }

        const carrier = String(data?.label_carrier || '').trim().toUpperCase();
        if (carrier) {
          setPickupLockedCarrier(carrier);
          setPickupCarrier(carrier);
        } else {
          setPickupLockedCarrier('');
        }
      } catch {
        if (!cancelled) {
          setPickupLockedCarrier('');
        }
      } finally {
        if (!cancelled) setPickupCarrierLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      try {
        window.clearTimeout(timeoutId);
      } catch {
        // ignore
      }
    };
  }, [pickupOrderNumber]);

  useEffect(() => {
    if (!jobId) return;
    const statusUpperNext = String(job?.status || '').toUpperCase();
    if (statusUpperNext !== 'SUCCEEDED' && statusUpperNext !== 'PARTIAL') return;
    if (refreshedCandidatesForJobRef.current[jobId]) return;
    refreshedCandidatesForJobRef.current[jobId] = true;
    loadLabelCandidates({ reset: true });
  }, [jobId, job?.status]);

  const selectedCount = useMemo(() => Object.values(selectedOrders).filter(Boolean).length, [selectedOrders]);

  const selectedOrderNumbers = useMemo(() => {
    if (!labelCandidates.length) return [];
    return labelCandidates
      .map((o) => String(o?.order_number || '').trim())
      .filter((n) => n && selectedOrders[n]);
  }, [labelCandidates, selectedOrders]);

  const normalizePickupDatetime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (raw.includes('T')) {
      try {
        const dt = new Date(raw);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toISOString().replace('.000Z', 'Z');
      } catch {
        return '';
      }
    }
    return raw;
  };

  const presetNote = 'Small: up to 0.75 lb (8×6×2 in). Medium: up to 2 lb (12×10×4 in). Large: up to 5 lb (16×12×8 in).';

  const validRows = useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const r of rawRows) {
      const order = String(r.order_number || '').trim();
      const tracking = String(r.tracking_number || '').trim();
      if (!order || !tracking) continue;
      const key = `${order}::${tracking}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ order_number: order, tracking_number: tracking });
    }
    return out;
  }, [rawRows]);

  const invalidCount = rawRows.length - validRows.length;

  const statusUpper = String(job?.status || '').toUpperCase();
  const isJobTerminal = statusUpper === 'SUCCEEDED' || statusUpper === 'PARTIAL' || statusUpper === 'FAILED';
  const isJobInProgress = Boolean(jobId) && !isJobTerminal;
  const totalForProgress = Number(job?.total ?? 0) || 0;
  const processedForProgress = Number(job?.processed ?? 0) || 0;
  const progressPct = totalForProgress > 0 ? Math.min(100, Math.max(0, Math.round((processedForProgress / totalForProgress) * 100))) : 0;

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Orders/Reports');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
      } catch (authError) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  useEffect(() => {
    if (!jobId) return undefined;

    let stopped = false;
    let interval;
    let lastNextIndex = null;
    let lastProcessed = null;
    let unchangedCount = 0;
    const startedAtMs = Date.now();

    const stopPolling = () => {
      if (interval) window.clearInterval(interval);
      interval = null;
      stopped = true;
    };

    const tick = async () => {
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        const res = await fetch(`${API_BASE_URL}/admin/shipments/jobs/${encodeURIComponent(jobId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load job status');

        if (stopped) return;
        const nextJob = data.job || null;
        setJob(nextJob);
        setDownloadUrl(data.download_url || '');

        const status = String(nextJob?.status || '').toUpperCase();
        if (status === 'SUCCEEDED' || status === 'PARTIAL' || status === 'FAILED') {
          stopPolling();
          return;
        }

        const nextIndex = nextJob?.next_index ?? null;
        const processed = nextJob?.processed ?? null;
        const same = nextIndex === lastNextIndex && processed === lastProcessed;
        if (same) {
          unchangedCount += 1;
        } else {
          unchangedCount = 0;
          lastNextIndex = nextIndex;
          lastProcessed = processed;
        }

        const elapsedMs = Date.now() - startedAtMs;
        if (unchangedCount >= 10 || elapsedMs >= 10 * 60 * 1000) {
          stopPolling();
          setError('Stopped polling job status to prevent excessive requests. Refresh to check again.');
        }
      } catch (e) {
        if (!stopped) {
          setError(e?.message || 'Failed to load job status');
        }
      }
    };

    interval = window.setInterval(tick, 3000);
    tick();

    return () => {
      stopPolling();
    };
  }, [jobId]);

  const openReport = async () => {
    if (!jobId) return;
    if (!API_BASE_URL) return;
    try {
      setReportOpen(true);
      setReportLoading(true);
      setReportError('');
      setReportRows([]);

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/shipments/jobs/${encodeURIComponent(jobId)}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load report');
      setReportRows(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setReportError(e?.message || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const schedulePickup = async () => {
    setError('');
    setStatusMessage('');
    setPickupResult(null);
    setPickupError('');
    setPickupStatusMessage('');
    setPickupFieldErrors({});

    if (!API_BASE_URL) {
      const msg = 'Missing API base URL (REACT_APP_ADMIN_API_URL).';
      setError(msg);
      setPickupError(msg);
      return;
    }

    const order_number = String(pickupOrderNumber || '').trim();
    if (!order_number) {
      const msg = 'Order number is required.';
      setError(msg);
      setPickupError(msg);
      setPickupFieldErrors({ order_number: msg });
      return;
    }

    const min_datetime = normalizePickupDatetime(pickupMin);
    const max_datetime = normalizePickupDatetime(pickupMax);
    if (!min_datetime || !max_datetime) {
      const msg = 'Pickup window (start and end) is required.';
      setError(msg);
      setPickupError(msg);
      setPickupFieldErrors({
        ...(min_datetime ? {} : { min_datetime: 'Required' }),
        ...(max_datetime ? {} : { max_datetime: 'Required' }),
      });
      return;
    }

    if (new Date(min_datetime).getTime() >= new Date(max_datetime).getTime()) {
      const msg = 'Pickup window end must be after start.';
      setError(msg);
      setPickupError(msg);
      setPickupFieldErrors({ min_datetime: 'Must be before end', max_datetime: 'Must be after start' });
      return;
    }

    const addressOverrideEnabled = Boolean(pickupUseAddressOverride);
    const address_override = addressOverrideEnabled
      ? {
          street1: String(pickupStreet1 || '').trim(),
          street2: String(pickupStreet2 || '').trim(),
          city: String(pickupCity || '').trim(),
          state: String(pickupState || '').trim(),
          zip: String(pickupZip || '').trim(),
          country: String(pickupCountry || 'US').trim() || 'US',
          phone: String(pickupPhone || '').trim(),
          email: String(pickupEmail || '').trim(),
        }
      : null;

    if (addressOverrideEnabled) {
      const missing = [];
      const fieldErrors = {};
      if (!address_override.street1) missing.push('Street address');
      if (!address_override.city) missing.push('City');
      if (!address_override.state) missing.push('State');
      if (!address_override.zip) missing.push('Zip');
      if (!address_override.country) missing.push('Country');
      if (!address_override.phone) missing.push('Phone');
      if (missing.length) {
        const msg = `Pickup address override is missing: ${missing.join(', ')}`;
        setError(msg);
        setPickupError(msg);
        if (!address_override.street1) fieldErrors.street1 = 'Required';
        if (!address_override.city) fieldErrors.city = 'Required';
        if (!address_override.state) fieldErrors.state = 'Required';
        if (!address_override.zip) fieldErrors.zip = 'Required';
        if (!address_override.country) fieldErrors.country = 'Required';
        if (!address_override.phone) fieldErrors.phone = 'Required';
        setPickupFieldErrors(fieldErrors);
        return;
      }
    }

    try {
      setPickupSubmitting(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/shipments/pickups`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number,
          carrier: pickupCarrier,
          min_datetime,
          max_datetime,
          instructions: pickupInstructions,
          address_override: addressOverrideEnabled ? address_override : undefined,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to schedule pickup'));

      setPickupResult(data);
      setStatusMessage('Pickup scheduled.');
      setPickupStatusMessage('Pickup scheduled.');

      setPickupOrderNumber('');
      setPickupCarrier('ANY');
      setPickupMin('');
      setPickupMax('');
      setPickupInstructions('');
      setPickupUseAddressOverride(false);
      setPickupStreet1('');
      setPickupStreet2('');
      setPickupCity('');
      setPickupState('');
      setPickupZip('');
      setPickupCountry('US');
      setPickupPhone('');
      setPickupEmail('');
      setPickupFieldErrors({});
      scrollToPickupStatus();
    } catch (e) {
      const msg = e?.message || 'Failed to schedule pickup';
      setError(msg);
      setPickupError(msg);
    } finally {
      setPickupSubmitting(false);
    }
  };

  const refreshPickupStatus = async () => {
    const pickupId = String(pickupResult?.pickup?.id || '').trim();
    if (!pickupId) return;
    if (!API_BASE_URL) return;

    try {
      setPickupSubmitting(true);
      setPickupError('');
      setPickupStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/shipments/pickups/${encodeURIComponent(pickupId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to refresh pickup status'));

      setPickupResult((prev) => ({
        ...(prev || {}),
        pickup: data?.pickup || prev?.pickup,
      }));
      setPickupStatusMessage('Pickup status refreshed.');
      scrollToPickupStatus();
    } catch (e) {
      const msg = e?.message || 'Failed to refresh pickup status';
      setError(msg);
      setPickupError(msg);
      scrollToPickupStatus();
    } finally {
      setPickupSubmitting(false);
    }
  };

  const closeReport = () => {
    setReportOpen(false);
    setReportLoading(false);
    setReportError('');
    setReportRows([]);
  };

  const openPrint = (url) => {
    const next = String(url || '').trim();
    if (!next) return;
    setPrintLabelUrl(next);
    setPrintOpen(true);
  };

  const closePrint = () => {
    setPrintOpen(false);
    setPrintLabelUrl('');
  };

  const clearOnKeyDown = (e, onClear) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    e.preventDefault();
    if (typeof onClear === 'function') onClear();
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch {
      // ignore
    }
  };

  const handleFileChange = async (event) => {
    setError('');
    setStatusMessage('');
    setJobId('');
    setJob(null);
    setDownloadUrl('');

    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name || '');

    const text = await file.text();
    const parsed = parseCsv(text);
    if (parsed.error) {
      setError(parsed.error);
      setRawRows([]);
      return;
    }

    setRawRows(parsed.rows);
    setStatusMessage(`Loaded ${parsed.rows.length} row(s).`);
  };

  const loadLabelCandidates = async ({ reset }) => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    try {
      setLoadingCandidates(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = new URL(`${API_BASE_URL}/admin/shipments/label-candidates`);
      url.searchParams.set('lookback_days', String(labelLookbackDays || 7));
      url.searchParams.set('limit', '100');
      if (!reset && labelCursor) {
        url.searchParams.set('cursor', labelCursor);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load orders');

      const list = Array.isArray(data?.orders) ? data.orders : [];
      setLabelCandidates((prev) => (reset ? list : [...prev, ...list]));
      setLabelCursor(String(data?.next_cursor || ''));
      if (reset) {
        setSelectedOrders({});
        setRowOverrides({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load orders');
      if (reset) {
        setLabelCandidates([]);
        setLabelCursor('');
        setSelectedOrders({});
        setRowOverrides({});
      }
    } finally {
      setLoadingCandidates(false);
    }
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedOrders({});
      return;
    }
    const next = {};
    for (const o of labelCandidates) {
      const num = String(o?.order_number || '').trim();
      if (num) next[num] = true;
    }
    setSelectedOrders(next);
  };

  const ensureOverride = (orderNumber) => {
    setRowOverrides((prev) => {
      if (prev[orderNumber]) return prev;
      return {
        ...prev,
        [orderNumber]: {
          enabled: true,
          mode: globalMode,
          preset: globalPreset,
          carrier: globalCarrier,
          speed: globalSpeed,
          manual: { weight_lb: '', l: '', w: '', h: '' },
        },
      };
    });
  };

  const updateOverride = (orderNumber, patch) => {
    setRowOverrides((prev) => {
      const current = prev[orderNumber] || {
        enabled: true,
        mode: globalMode,
        preset: globalPreset,
        carrier: globalCarrier,
        speed: globalSpeed,
        manual: { weight_lb: '', l: '', w: '', h: '' },
      };
      return { ...prev, [orderNumber]: { ...current, ...patch } };
    });
  };

  const updateManual = (orderNumber, patch) => {
    setRowOverrides((prev) => {
      const current = prev[orderNumber] || {
        enabled: true,
        mode: 'manual',
        preset: globalPreset,
        manual: { weight_lb: '', l: '', w: '', h: '' },
      };
      return { ...prev, [orderNumber]: { ...current, manual: { ...(current.manual || {}), ...patch } } };
    });
  };

  const createLabelJob = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    const selected = labelCandidates
      .map((o) => String(o?.order_number || '').trim())
      .filter((n) => n && selectedOrders[n]);

    if (!selected.length) {
      setError('Please select at least one order.');
      return;
    }

    const pickup_enabled = Boolean(labelPickupEnabled);
    const schedule_pickup = pickup_enabled
      ? {
          enabled: true,
          carrier: String(labelPickupCarrier || 'ANY').toUpperCase(),
          min_datetime: normalizePickupDatetime(labelPickupMin),
          max_datetime: normalizePickupDatetime(labelPickupMax),
          instructions: String(labelPickupInstructions || '').trim(),
        }
      : { enabled: false };

    if (pickup_enabled) {
      if (!schedule_pickup.min_datetime || !schedule_pickup.max_datetime) {
        setError('Auto-schedule pickup requires pickup window start and end.');
        return;
      }

      if (new Date(schedule_pickup.min_datetime).getTime() >= new Date(schedule_pickup.max_datetime).getTime()) {
        setError('Auto-schedule pickup window end must be after start.');
        return;
      }

      const useOverride = Boolean(labelPickupUseAddressOverride);
      if (useOverride) {
        const address_override = {
          street1: String(labelPickupStreet1 || '').trim(),
          street2: String(labelPickupStreet2 || '').trim(),
          city: String(labelPickupCity || '').trim(),
          state: String(labelPickupState || '').trim(),
          zip: String(labelPickupZip || '').trim(),
          country: String(labelPickupCountry || 'US').trim() || 'US',
          phone: String(labelPickupPhone || '').trim(),
          email: String(labelPickupEmail || '').trim(),
        };

        const missing = [];
        if (!address_override.street1) missing.push('Street address');
        if (!address_override.city) missing.push('City');
        if (!address_override.state) missing.push('State');
        if (!address_override.zip) missing.push('Zip');
        if (!address_override.country) missing.push('Country');
        if (!address_override.phone) missing.push('Phone');
        if (missing.length) {
          setError(`Auto-schedule pickup address override is missing: ${missing.join(', ')}`);
          return;
        }

        schedule_pickup.address_override = address_override;
      }
    }

    const payloadOrders = selected.map((order_number) => {
      const override = rowOverrides[order_number];
      const effective = override?.enabled
        ? override
        : { mode: globalMode, preset: globalPreset, carrier: globalCarrier, speed: globalSpeed, manual: { weight_lb: '', l: '', w: '', h: '' } };

      const mode = String(effective?.mode || 'preset').toLowerCase();
      const preset = String(effective?.preset || globalPreset || 'SMALL').toUpperCase();
      const carrier = String(effective?.carrier || globalCarrier || 'ANY').toUpperCase();
      const speed = String(effective?.speed || globalSpeed || 'STANDARD').toUpperCase();
      const manual = effective?.manual || {};

      return {
        order_number,
        mode,
        preset,
        carrier,
        speed,
        fallback_to_cheapest: true,
        manual: {
          weight_lb: manual.weight_lb,
          dims_in: { l: manual.l, w: manual.w, h: manual.h },
        },
      };
    });

    try {
      setSubmitting(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/shipments/labels/jobs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orders: payloadOrders, send_email: sendEmail, schedule_pickup }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to create label job'));

      setJobId(data.job_id || '');
      setStatusMessage(`Label job started: ${data.job_id}`);
      scrollToJobStatus();
    } catch (e) {
      setError(e?.message || 'Failed to create label job');
    } finally {
      setSubmitting(false);
    }
  };

  const submitJob = async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (!validRows.length) {
      setError('No valid rows found.');
      return;
    }

    try {
      setSubmitting(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/shipments/jobs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: validRows,
          send_email: sendEmail,
          resend_if_already_shipped: resendIfAlreadyShipped,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to create job'));

      setJobId(data.job_id || '');
      setStatusMessage(`Job started: ${data.job_id}`);
      scrollToJobStatus();
    } catch (e) {
      setError(e?.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-truck"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Shipments</h1>
            <p>Bulk upload tracking numbers</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        <div className="product-form-container">
          <details open className="product-form-accordion">
            <summary className="product-form-accordion-summary">
              <div className="product-form-accordion-summary-inner">
                <div className="product-form-accordion-title-row">
                  <i className="fas fa-caret-right product-form-accordion-caret" aria-hidden="true"></i>
                  <div className="product-form-accordion-title">Provider-based Shipment</div>
                </div>
                <div className="field-hint product-form-accordion-subtitle">Create shipping labels</div>
              </div>
            </summary>

            <div style={{ marginTop: 12 }}>
              <fieldset
                disabled={isJobInProgress}
                style={{ border: 0, padding: 0, margin: 0, opacity: isJobInProgress ? 0.55 : 1 }}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label>Lookback (days)</label>
                    <input
                      className="card-input"
                      type="number"
                      min="1"
                      max="365"
                      value={labelLookbackDays}
                      onChange={(e) => setLabelLookbackDays(e.target.value)}
                    />
                    <p className="field-hint">Loads backlog orders paid within the last N days.</p>
                  </div>
                  <div className="form-group">
                    <label>Global mode</label>
                    <select
                      className="card-input"
                      value={globalMode}
                      onChange={(e) => setGlobalMode(e.target.value)}
                    >
                      <option value="preset">Presets</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Global preset</label>
                    <select
                      className="card-input"
                      value={globalPreset}
                      onChange={(e) => setGlobalPreset(e.target.value)}
                      disabled={globalMode !== 'preset'}
                    >
                      <option value="SMALL">Small</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LARGE">Large</option>
                    </select>
                    <p className="field-hint">{presetNote}</p>
                  </div>
                  <div className="form-group">
                    <label>Global carrier</label>
                    <select
                      className="card-input"
                      value={globalCarrier}
                      onChange={(e) => setGlobalCarrier(e.target.value)}
                    >
                      <option value="ANY">Any</option>
                      <option value="USPS">USPS</option>
                      <option value="UPS">UPS</option>
                      <option value="FEDEX">FedEx</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Global speed</label>
                    <select
                      className="card-input"
                      value={globalSpeed}
                      onChange={(e) => setGlobalSpeed(e.target.value)}
                    >
                      <option value="STANDARD">Standard</option>
                      <option value="2DAY">2-Day</option>
                      <option value="OVERNIGHT">Overnight</option>
                      <option value="ANY">Any</option>
                    </select>
                    <p className="field-hint">
                      If no matching rate is available for the selected carrier/speed, the system will first try the same speed with other carriers.
                      If still unavailable, it will try the selected carrier with any speed, and finally fall back to the cheapest available rate.
                    </p>
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="checkbox" checked={labelPickupEnabled} onChange={(e) => setLabelPickupEnabled(e.target.checked)} />
                      Auto-schedule pickup after label creation
                    </label>
                    <p className="field-hint">
                      If enabled, a pickup will be scheduled after each label is successfully created.
                      Default pickup address uses the business ship-from address configured during setup.
                    </p>
                  </div>

                  {labelPickupEnabled ? (
                    <>
                      <div className="form-group">
                        <label>Pickup carrier (optional)</label>
                        <select className="card-input" value={labelPickupCarrier} onChange={(e) => setLabelPickupCarrier(e.target.value)}>
                          <option value="ANY">Any</option>
                          <option value="USPS">USPS</option>
                          <option value="UPS">UPS</option>
                          <option value="FEDEX">FedEx</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Pickup window start</label>
                        <input className="card-input" type="datetime-local" value={labelPickupMin} onChange={(e) => setLabelPickupMin(e.target.value)} />
                      </div>

                      <div className="form-group">
                        <label>Pickup window end</label>
                        <input className="card-input" type="datetime-local" value={labelPickupMax} onChange={(e) => setLabelPickupMax(e.target.value)} />
                      </div>

                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Pickup instructions (optional)</label>
                        <textarea
                          className="card-input"
                          rows={2}
                          value={labelPickupInstructions}
                          onChange={(e) => setLabelPickupInstructions(e.target.value)}
                        />
                      </div>

                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={labelPickupUseAddressOverride}
                            onChange={(e) => setLabelPickupUseAddressOverride(e.target.checked)}
                          />
                          Use different pickup address
                        </label>
                        <p className="field-hint">
                          Override format requires: street, city, state, zip, country, phone.
                        </p>
                      </div>

                      {labelPickupUseAddressOverride ? (
                        <>
                          <div className="form-group">
                            <label>Street address</label>
                            <input className="card-input" type="text" value={labelPickupStreet1} onChange={(e) => setLabelPickupStreet1(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Apt / Suite (optional)</label>
                            <input className="card-input" type="text" value={labelPickupStreet2} onChange={(e) => setLabelPickupStreet2(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>City</label>
                            <input className="card-input" type="text" value={labelPickupCity} onChange={(e) => setLabelPickupCity(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>State</label>
                            <input className="card-input" type="text" value={labelPickupState} onChange={(e) => setLabelPickupState(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Zip</label>
                            <input className="card-input" type="text" value={labelPickupZip} onChange={(e) => setLabelPickupZip(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Country</label>
                            <input className="card-input" type="text" value={labelPickupCountry} onChange={(e) => setLabelPickupCountry(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Phone</label>
                            <input className="card-input" type="tel" value={labelPickupPhone} onChange={(e) => setLabelPickupPhone(e.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Email (optional)</label>
                            <input className="card-input" type="email" value={labelPickupEmail} onChange={(e) => setLabelPickupEmail(e.target.value)} />
                          </div>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" disabled={loadingCandidates} onClick={() => loadLabelCandidates({ reset: true })}>
                    <i className="fas fa-sync"></i>
                    Load orders
                  </button>
                  <button type="button" className="btn-submit" disabled={submitting || selectedCount === 0} onClick={createLabelJob}>
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Starting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-tag"></i>
                        Create labels ({selectedCount})
                      </>
                    )}
                  </button>
                </div>

                {labelCandidates.length ? (
                  <div className="audit-table-wrapper shipments-provider-table" style={{ marginTop: 18 }}>
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedCount > 0 && selectedCount === labelCandidates.length}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th>Order</th>
                    <th>Paid at</th>
                    <th>Status</th>
                    <th>Package</th>
                    <th>Shipping</th>
                    <th>Override</th>
                  </tr>
                </thead>
                <tbody>
                  {labelCandidates.map((o) => {
                    const num = String(o?.order_number || '').trim();
                    const override = rowOverrides[num];
                    const useOverride = Boolean(override?.enabled);
                    const effectiveMode = useOverride ? String(override?.mode || 'preset') : globalMode;
                    const effectivePreset = useOverride ? String(override?.preset || 'SMALL') : globalPreset;
                    const effectiveCarrier = useOverride ? String(override?.carrier || 'ANY') : globalCarrier;
                    const effectiveSpeed = useOverride ? String(override?.speed || 'STANDARD') : globalSpeed;
                    const manual = override?.manual || { weight_lb: '', l: '', w: '', h: '' };

                    return (
                      <tr key={num}>
                        <td data-label="Select">
                          <input
                            type="checkbox"
                            checked={Boolean(selectedOrders[num])}
                            onChange={(e) => setSelectedOrders((prev) => ({ ...prev, [num]: e.target.checked }))}
                          />
                        </td>
                        <td className="mono" data-label="Order">{num}</td>
                        <td data-label="Paid at">{o?.paid_at ? new Date(Number(o.paid_at) * 1000).toLocaleString('en-US', { timeZone: 'America/New_York' }) : 'N/A'}</td>
                        <td data-label="Status">{String(o?.status || 'Processing')}</td>
                        <td data-label="Package">
                          {effectiveMode === 'preset' ? (
                            <select
                              className="card-input"
                              value={effectivePreset}
                              disabled={!useOverride}
                              onChange={(e) => updateOverride(num, { preset: e.target.value })}
                            >
                              <option value="SMALL">Small</option>
                              <option value="MEDIUM">Medium</option>
                              <option value="LARGE">Large</option>
                            </select>
                          ) : (
                            <div className="shipments-package-manual-grid">
                              <input
                                className="card-input"
                                placeholder="lb"
                                disabled={!useOverride}
                                value={manual.weight_lb}
                                onChange={(e) => updateManual(num, { weight_lb: e.target.value })}
                              />
                              <input className="card-input" placeholder="L" disabled={!useOverride} value={manual.l} onChange={(e) => updateManual(num, { l: e.target.value })} />
                              <input className="card-input" placeholder="W" disabled={!useOverride} value={manual.w} onChange={(e) => updateManual(num, { w: e.target.value })} />
                              <input className="card-input" placeholder="H" disabled={!useOverride} value={manual.h} onChange={(e) => updateManual(num, { h: e.target.value })} />
                            </div>
                          )}

                          {useOverride ? (
                            <div style={{ marginTop: 8 }}>
                              <select
                                className="card-input"
                                value={effectiveMode}
                                onChange={(e) => updateOverride(num, { mode: e.target.value })}
                              >
                                <option value="preset">Preset</option>
                                <option value="manual">Manual</option>
                              </select>
                            </div>
                          ) : null}
                        </td>
                        <td data-label="Shipping">
                          <select
                            className="card-input"
                            value={effectiveCarrier}
                            disabled={!useOverride}
                            onChange={(e) => updateOverride(num, { carrier: e.target.value })}
                          >
                            <option value="ANY">Any</option>
                            <option value="USPS">USPS</option>
                            <option value="UPS">UPS</option>
                            <option value="FEDEX">FedEx</option>
                          </select>
                          <div style={{ marginTop: 8 }}>
                            <select
                              className="card-input"
                              value={effectiveSpeed}
                              disabled={!useOverride}
                              onChange={(e) => updateOverride(num, { speed: e.target.value })}
                            >
                              <option value="STANDARD">Standard</option>
                              <option value="2DAY">2-Day</option>
                              <option value="OVERNIGHT">Overnight</option>
                              <option value="ANY">Any</option>
                            </select>
                          </div>
                        </td>
                        <td data-label="Override">
                          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="checkbox"
                              checked={useOverride}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  ensureOverride(num);
                                }
                                updateOverride(num, { enabled: checked });
                              }}
                            />
                            Override
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {labelCursor ? (
                <div className="form-actions" style={{ marginTop: 12 }}>
                  <button type="button" className="btn-secondary" disabled={loadingCandidates} onClick={() => loadLabelCandidates({ reset: false })}>
                    Load more
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="field-hint" style={{ marginTop: 12 }}>
              Load orders to select which ones to create shipping labels for.
            </div>
          )}
              </fieldset>
            </div>
          </details>
        </div>

        <div className="product-form-container">
          <details open className="product-form-accordion">
            <summary className="product-form-accordion-summary">
              <div className="product-form-accordion-summary-inner">
                <div className="product-form-accordion-title-row">
                  <i className="fas fa-caret-right product-form-accordion-caret" aria-hidden="true"></i>
                  <div className="product-form-accordion-title">Schedule Pickup</div>
                </div>
                <div className="field-hint product-form-accordion-subtitle">Request a carrier pickup for labeled shipments</div>
              </div>
            </summary>

            <div style={{ marginTop: 12 }}>
              {pickupStatusMessage ? (
                <div className="alert alert-success" style={{ marginBottom: 12 }} ref={pickupStatusRef} tabIndex={-1}>
                  <i className="fas fa-check-circle"></i>
                  {pickupStatusMessage}
                </div>
              ) : null}

              {pickupError ? (
                <div className="alert alert-error" style={{ marginBottom: 12 }} ref={pickupStatusRef} tabIndex={-1}>
                  <i className="fas fa-exclamation-circle"></i>
                  {pickupError}
                </div>
              ) : null}

              <fieldset disabled={pickupSubmitting} style={{ border: 0, padding: 0, margin: 0, opacity: pickupSubmitting ? 0.55 : 1 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Order number</label>
                    <input
                      className={`card-input${pickupFieldErrors.order_number ? ' bulk-input-error' : ''}`}
                      type="text"
                      placeholder="e.g. 100123"
                      value={pickupOrderNumber}
                      onChange={(e) => setPickupOrderNumber(e.target.value)}
                    />
                    {pickupFieldErrors.order_number ? <div className="bulk-field-error">{pickupFieldErrors.order_number}</div> : null}
                    {selectedOrderNumbers.length === 1 ? (
                      <div className="field-hint" style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ padding: '8px 12px' }}
                          onClick={() => setPickupOrderNumber(selectedOrderNumbers[0])}
                        >
                          Use selected order ({selectedOrderNumbers[0]})
                        </button>
                      </div>
                    ) : selectedOrderNumbers.length > 1 ? (
                      <p className="field-hint">Select exactly one order above to auto-fill here.</p>
                    ) : (
                      <p className="field-hint">Pickups require an order with an EasyPost label (shipment id).</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>{pickupLockedCarrier ? 'Carrier (from label)' : 'Carrier (optional)'}</label>
                    <select
                      className="card-input"
                      value={pickupCarrier}
                      disabled={Boolean(pickupLockedCarrier) || pickupCarrierLoading}
                      onChange={(e) => setPickupCarrier(e.target.value)}
                    >
                      <option value="ANY">Any</option>
                      <option value="USPS">USPS</option>
                      <option value="UPS">UPS</option>
                      <option value="FEDEX">FedEx</option>
                    </select>
                    <p className="field-hint">
                      {pickupLockedCarrier
                        ? `This order’s label carrier is ${pickupLockedCarrier}. Pickup carrier selection is locked.`
                        : 'We’ll pick the cheapest pickup rate that matches your carrier selection.'}
                    </p>
                  </div>

                  <div className="form-group">
                    <label>Pickup window start</label>
                    <input
                      className={`card-input${pickupFieldErrors.min_datetime ? ' bulk-input-error' : ''}`}
                      type="datetime-local"
                      value={pickupMin}
                      onChange={(e) => setPickupMin(e.target.value)}
                    />
                    {pickupFieldErrors.min_datetime ? <div className="bulk-field-error">{pickupFieldErrors.min_datetime}</div> : null}
                    <p className="field-hint">Use your local time window (carrier availability varies by zip).</p>
                  </div>

                  <div className="form-group">
                    <label>Pickup window end</label>
                    <input
                      className={`card-input${pickupFieldErrors.max_datetime ? ' bulk-input-error' : ''}`}
                      type="datetime-local"
                      value={pickupMax}
                      onChange={(e) => setPickupMax(e.target.value)}
                    />
                    {pickupFieldErrors.max_datetime ? <div className="bulk-field-error">{pickupFieldErrors.max_datetime}</div> : null}
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Instructions (optional)</label>
                    <textarea
                      className="card-input"
                      rows={3}
                      placeholder="e.g. Ring bell, packages at front desk, pickup from loading dock"
                      value={pickupInstructions}
                      onChange={(e) => setPickupInstructions(e.target.value)}
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={pickupUseAddressOverride}
                        onChange={(e) => setPickupUseAddressOverride(e.target.checked)}
                      />
                      Use different pickup address
                    </label>
                    <p className="field-hint">
                      Default uses the business ship-from address configured during setup. Override format requires:
                      street, city, state, zip, country, phone.
                    </p>
                  </div>

                  {pickupUseAddressOverride ? (
                    <>
                      <div className="form-group">
                        <label>Street address</label>
                        <input
                          className={`card-input${pickupFieldErrors.street1 ? ' bulk-input-error' : ''}`}
                          type="text"
                          value={pickupStreet1}
                          onChange={(e) => setPickupStreet1(e.target.value)}
                        />
                        {pickupFieldErrors.street1 ? <div className="bulk-field-error">{pickupFieldErrors.street1}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>Apt / Suite (optional)</label>
                        <input className="card-input" type="text" value={pickupStreet2} onChange={(e) => setPickupStreet2(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>City</label>
                        <input
                          className={`card-input${pickupFieldErrors.city ? ' bulk-input-error' : ''}`}
                          type="text"
                          value={pickupCity}
                          onChange={(e) => setPickupCity(e.target.value)}
                        />
                        {pickupFieldErrors.city ? <div className="bulk-field-error">{pickupFieldErrors.city}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>State</label>
                        <input
                          className={`card-input${pickupFieldErrors.state ? ' bulk-input-error' : ''}`}
                          type="text"
                          value={pickupState}
                          onChange={(e) => setPickupState(e.target.value)}
                        />
                        {pickupFieldErrors.state ? <div className="bulk-field-error">{pickupFieldErrors.state}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>Zip</label>
                        <input
                          className={`card-input${pickupFieldErrors.zip ? ' bulk-input-error' : ''}`}
                          type="text"
                          value={pickupZip}
                          onChange={(e) => setPickupZip(e.target.value)}
                        />
                        {pickupFieldErrors.zip ? <div className="bulk-field-error">{pickupFieldErrors.zip}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          className={`card-input${pickupFieldErrors.country ? ' bulk-input-error' : ''}`}
                          type="text"
                          value={pickupCountry}
                          onChange={(e) => setPickupCountry(e.target.value)}
                        />
                        {pickupFieldErrors.country ? <div className="bulk-field-error">{pickupFieldErrors.country}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          className={`card-input${pickupFieldErrors.phone ? ' bulk-input-error' : ''}`}
                          type="tel"
                          value={pickupPhone}
                          onChange={(e) => setPickupPhone(e.target.value)}
                        />
                        {pickupFieldErrors.phone ? <div className="bulk-field-error">{pickupFieldErrors.phone}</div> : null}
                      </div>
                      <div className="form-group">
                        <label>Email (optional)</label>
                        <input className="card-input" type="email" value={pickupEmail} onChange={(e) => setPickupEmail(e.target.value)} />
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-submit" onClick={schedulePickup} disabled={pickupSubmitting}>
                    {pickupSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-calendar-check"></i>
                        Schedule pickup
                      </>
                    )}
                  </button>
                </div>

                {pickupResult?.pickup ? (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>Pickup confirmation</div>
                    <div style={{ display: 'grid', gap: 6, color: '#344054' }}>
                      <div>
                        <strong>Status:</strong> {String(pickupResult.pickup.status || 'unknown')}
                      </div>
                      <div>
                        <strong>Confirmation:</strong> {String(pickupResult.pickup.confirmation || '—')}
                      </div>
                      <div>
                        <strong>Requested carrier:</strong> {String(pickupResult.requested_carrier || '—')}
                      </div>
                      <div>
                        <strong>Carrier / Service:</strong> {String(pickupResult.selected_rate?.carrier || '—')} / {String(pickupResult.selected_rate?.service || '—')}
                      </div>
                      <div>
                        <strong>Rate:</strong> {String(pickupResult.selected_rate?.rate || '—')} {String(pickupResult.selected_rate?.currency || '')}
                      </div>
                    </div>

                    <div className="form-actions" style={{ marginTop: 12 }}>
                      <button type="button" className="btn-secondary" onClick={refreshPickupStatus} disabled={pickupSubmitting}>
                        Refresh pickup status
                      </button>
                    </div>
                  </div>
                ) : null}
              </fieldset>
            </div>
          </details>
        </div>

        <div className="product-form-container">
          <details open className="product-form-accordion">
            <summary className="product-form-accordion-summary">
              <div className="product-form-accordion-summary-inner">
                <div className="product-form-accordion-title-row">
                  <i className="fas fa-caret-right product-form-accordion-caret" aria-hidden="true"></i>
                  <div className="product-form-accordion-title">Manual Shipment</div>
                </div>
                <div className="field-hint product-form-accordion-subtitle">Upload CSV</div>
              </div>
            </summary>

            <div style={{ marginTop: 12 }}>
              <div className="form-group">
                <label>File (CSV with headers: order_number, tracking_number)</label>
                <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
                {fileName ? <p className="field-hint">Selected: {fileName}</p> : null}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                    Send shipment email
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={resendIfAlreadyShipped}
                      onChange={(e) => setResendIfAlreadyShipped(e.target.checked)}
                    />
                    Resend if already shipped
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-submit" disabled={submitting || !validRows.length} onClick={submitJob}>
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Starting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play"></i>
                      Start job ({validRows.length} rows)
                    </>
                  )}
                </button>
              </div>

              {rawRows.length ? (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontWeight: 800, marginBottom: 10 }}>Preview</div>
                  <div style={{ color: '#667085', marginBottom: 10 }}>
                    Total rows: {rawRows.length}. Valid: {validRows.length}. Ignored: {invalidCount}.
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="audit-table" style={{ minWidth: 560 }}>
                      <thead>
                        <tr>
                          <th>Line</th>
                          <th>Order Number</th>
                          <th>Tracking Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validRows.map((row) => (
                          <tr key={`${row.order_number}-${row.line}`}>
                            <td className="mono">{row.line}</td>
                            <td className="mono">{row.order_number}</td>
                            <td className="mono">{row.tracking_number}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </details>
        </div>

        {jobId ? (
          <div className="products-list-container" ref={jobStatusRef} tabIndex={-1} style={{ outline: 'none' }}>
            <h2>Job Status</h2>
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <strong>Job ID:</strong> {jobId}
              </div>
              <div>
                <strong>Status:</strong>{' '}
                {isJobTerminal ? (
                  <span className={`job-status-pill ${statusUpper === 'FAILED' ? 'failed' : 'success'}`}>
                    <i className={`fas ${statusUpper === 'FAILED' ? 'fa-times-circle' : 'fa-check-circle'}`}></i>
                    {job?.status || 'Completed'}
                  </span>
                ) : (
                  <span className="job-status-pill running">
                    <i className="fas fa-spinner fa-spin"></i>
                    {job?.status || 'Loading...'}
                  </span>
                )}
              </div>
              <div>
                <strong>Processed:</strong> {job?.processed ?? 0} / {job?.total ?? 0}
              </div>
              {isJobInProgress ? (
                <div className="job-progress">
                  <div className="job-progress-bar running" style={{ width: `${progressPct}%` }}></div>
                </div>
              ) : jobId ? (
                <div className="job-progress">
                  <div className={`job-progress-bar ${statusUpper === 'FAILED' ? 'failed' : 'success'}`} style={{ width: '100%' }}></div>
                </div>
              ) : null}
              <div>
                <strong>Updated:</strong> {job?.updated ?? 0} | <strong>Skipped:</strong> {job?.skipped ?? 0} |{' '}
                <strong>Failed:</strong> {job?.failed ?? 0}
              </div>

              {downloadUrl ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <a href={downloadUrl} target="_blank" rel="noreferrer" className="btn-secondary" style={{ display: 'inline-flex' }}>
                    <i className="fas fa-download"></i>
                    Download CSV
                    </a>
                    <button type="button" className="btn-secondary" onClick={openReport}>
                      <i className="fas fa-eye"></i>
                      View report
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {reportOpen ? (
          <div className="fulfillment-modal-overlay" role="dialog" aria-modal="true">
            <div className="fulfillment-modal" style={{ width: 'min(1100px, 92vw)' }}>
              <div className="fulfillment-modal-header">
                <h3>Shipment Report</h3>
                <button className="admin-mini-btn" onClick={closeReport}>
                  Close
                </button>
              </div>

              <div className="fulfillment-modal-body">
                {reportError ? <div className="alert alert-error">{reportError}</div> : null}

                {reportLoading ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading report...
                  </div>
                ) : reportRows.length ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="audit-table" style={{ minWidth: 980 }}>
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Status</th>
                          <th>Carrier</th>
                          <th>Label Carrier</th>
                          <th>Service</th>
                          <th>Requested</th>
                          <th>Fallback</th>
                          <th>Tracking</th>
                          <th>Label</th>
                          <th>Rate</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportRows.map((r, idx) => {
                          const orderNum = String(r?.order_number || '').trim();
                          const tracking = String(r?.tracking_number || '').trim();
                          const trackUrl = String(r?.tracking_url || '').trim();
                          const labelUrl = String(r?.label_url || '').trim();
                          const service = String(r?.service || '').trim();
                          const requestedCarrier = String(r?.requested_carrier || '').trim();
                          const requestedSpeed = String(r?.requested_speed || '').trim();
                          const usedFallback = r?.used_fallback;
                          const labelCarrier = String(r?.label_carrier || '').trim();
                          return (
                            <tr key={`${orderNum || 'row'}-${idx}`}>
                              <td className="mono">{orderNum || '—'}</td>
                              <td>{String(r?.status || '')}</td>
                              <td>{String(r?.carrier || '')}</td>
                              <td>{labelCarrier || '—'}</td>
                              <td>{service || '—'}</td>
                              <td>
                                {requestedCarrier || requestedSpeed ? (
                                  <span>
                                    {requestedCarrier || '—'} / {requestedSpeed || '—'}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>{usedFallback === true ? 'Yes' : usedFallback === false ? 'No' : '—'}</td>
                              <td>
                                {tracking ? (
                                  trackUrl ? (
                                    <a href={trackUrl} target="_blank" rel="noreferrer" className="mono">
                                      {tracking}
                                    </a>
                                  ) : (
                                    <span className="mono">{tracking}</span>
                                  )
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                {labelUrl ? (
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <a href={labelUrl} target="_blank" rel="noreferrer">
                                      View
                                    </a>
                                    <a
                                      href={labelUrl}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        openPrint(labelUrl);
                                      }}
                                    >
                                      Print
                                    </a>
                                  </div>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>{r?.rate != null ? String(r.rate) : '—'}</td>
                              <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{String(r?.reason || '')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ color: '#667085' }}>No report rows yet.</div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <LabelPrintModal open={printOpen} labelUrl={printLabelUrl} onClose={closePrint} />
      </div>
    </div>
  );
}
